import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchUsers,
  inviteUser,
  updateUser,
  deactivateUser,
  fetchUserProperties,
  assignUserProperties,
  resetPassword,
  type InviteUserPayload,
  type UpdateUserPayload,
} from '@/services/api/users.api';

// ─── Users ──────────────────────────────────────────────────────────────────

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    staleTime: 30_000,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InviteUserPayload) => inviteUser(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['housekeeping-staff'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
      updateUser(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resetPassword(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUserProperties(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-properties', userId],
    queryFn: () => fetchUserProperties(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useAssignUserProperties() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, propertyIds }: { userId: string; propertyIds: string[] }) =>
      assignUserProperties(userId, propertyIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user-properties'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
